import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import { booleanEqual, point } from '@turf/turf';

import {
  cloneDeep,
  compact,
  countBy,
  each,
  filter,
  find,
  findIndex,
  forEach,
  has,
  isUndefined,
  map,
  slice,
  sortBy
} from 'lodash-es';

import { defaultWalkingFarmRadius } from '../../constants/walking-farm.constant';
import {
  WalkingFarmMainTabEnum,
  WalkingFarmNewTabEnum,
  WalkingFarmPropertiesOwnerStatusFilterEnum
} from '../../enums/walking-farm';
import { isBlackKnight } from '../../helpers/data-provider';
import {
  OwnerOccupiedFilter,
  PolygonFarmCoordinates,
  PropertyTypeFilter,
  RadiusFarmCoordinates,
  SavedFarmListItem,
  WalkingFarmAlert
} from '../../typings/walking-farm';
import * as fromPersistedStateActions from '../actions/persisted-state.actions';
import * as fromPropertyProfileActions from '../actions/property-profile.actions';
import * as fromSettingActions from '../actions/setting.actions';
import * as fromWalkingFarmActions from '../actions/walking-farm.actions';
import { StoredWalkingFarm } from '../models/walking-farm.model';

export const walkingFarmFeatureKey = 'walkingFarm';

const commonPropertyTypesFilters: PropertyTypeFilter[] = [
  {
    id: 1,
    label: 'Single Family Residential',
    values: ['1000', '1001', '1008', '1009', '1013', '1999']
  },
  {
    id: 2,
    label: 'Residential Vacant Land',
    values: ['8001']
  },
  { id: 3, label: 'Condominium Unit', values: ['1004'] },
  { id: 4, label: 'Mobile/Manufactured Home', values: ['1006', '1016'] },
  { id: 5, label: 'Townhouse/Rowhouse', values: ['1002', '1007'] },
  { id: 6, label: 'Duplex', values: ['1101'] },
  { id: 7, label: 'Triplex', values: ['1102'] },
  { id: 8, label: 'Fourplex', values: ['1103'] },
  { id: 9, label: 'MultiFamily', values: ['1110'] },
  { id: 10, label: 'Seasonal, Vacation Residence', values: ['1012'] },
  { id: 11, label: 'Zero Lot Line', values: ['1003', '1014'] },
  { id: 12, label: 'Cooperative', values: ['1005'] }
];

const blackKnightPropertyTypesFilters: PropertyTypeFilter[] = [
  {
    id: 13,
    label: 'Single Family Residential w/ADU',
    values: ['1022', '1023']
  }
];

function getRadiusInMetersFromMiles(miles: number) {
  return miles * 1609.344;
}

export interface State extends EntityState<StoredWalkingFarm> {
  addressSearchLocation: google.maps.LatLngLiteral | null;
  addressSearchText: string | null;
  drawFarmCenter: google.maps.LatLngLiteral | null;
  drawFarmCompleted: boolean;
  drawFarmPoints: google.maps.LatLngLiteral[];
  drawFarmRadius: number;
  drawFarmRadiusInMeters: number;
  drawMode: boolean;
  drawModeType: WalkingFarmNewTabEnum;
  farmAlertsEnabled: boolean;
  farmAlertsFarmName: string | null;
  farmAlertsLastAlertDate: Date | null;
  farmAlertsList: WalkingFarmAlert[];
  farmAlertsLoaded: boolean;
  filterAvailablePropertyTypes: PropertyTypeFilter[];
  filterByMaxBathrooms: number | null;
  filterByMaxBedrooms: number | null;
  filterByMaxLivingArea: number | null;
  filterByMinBathrooms: number | null;
  filterByMinBedrooms: number | null;
  filterByMinLivingArea: number | null;
  filterByNoSalesWithinYears: number;
  filterByOwnerOccupied: OwnerOccupiedFilter;
  filterByPropertyType: PropertyTypeFilter[];
  filterBySaleTypes: string[] | null;
  filtersAppliedNumber: number;
  mainSelectedTab: WalkingFarmMainTabEnum;
  propertyFarmAlertsLastAlertDate: Date | null;
  propertyFarmAlertsList: WalkingFarmAlert[];
  propertyFarmAlertsLoaded: boolean;
  savedFarms: SavedFarmListItem[];
  savedFarmsTextFilter: string;
  summaryErrorMessage: string | null;
  summaryMessages: string[];
  summaryTotal: number;
  summaryTurnoverRate: number | null;
  totalSavedFarms: number;
  viewFarmBoundariesVisible: boolean;
  walkingFarmKey: string | null;
}

export function selectWalkingFarmId(entity: StoredWalkingFarm) {
  return entity.farmId;
}

export const adapter: EntityAdapter<StoredWalkingFarm> = createEntityAdapter<StoredWalkingFarm>({
  selectId: selectWalkingFarmId
});

export const initialState: State = adapter.getInitialState({
  addressSearchLocation: null,
  addressSearchText: null,
  drawFarmCenter: null,
  drawFarmCompleted: false,
  drawFarmPoints: [],
  drawFarmRadius: defaultWalkingFarmRadius,
  drawFarmRadiusInMeters: getRadiusInMetersFromMiles(defaultWalkingFarmRadius),
  drawMode: false,
  drawModeType: WalkingFarmNewTabEnum.Polygon,
  farmAlertsEnabled: false,
  farmAlertsFarmName: null,
  farmAlertsLastAlertDate: null,
  farmAlertsList: [],
  farmAlertsLoaded: false,
  filterAvailablePropertyTypes: [],
  filterByMaxBathrooms: null,
  filterByMaxBedrooms: null,
  filterByMaxLivingArea: null,
  filterByMinBathrooms: null,
  filterByMinBedrooms: null,
  filterByMinLivingArea: null,
  filterByNoSalesWithinYears: 0,
  filterByOwnerOccupied: null,
  filterByPropertyType: [],
  filterBySaleTypes: null,
  filtersAppliedNumber: 0,
  mainSelectedTab: WalkingFarmMainTabEnum.New,
  propertyFarmAlertsLastAlertDate: null,
  propertyFarmAlertsList: [],
  propertyFarmAlertsLoaded: false,
  savedFarms: [],
  savedFarmsTextFilter: '',
  summaryErrorMessage: null,
  summaryMessages: [],
  summaryTotal: 0,
  summaryTurnoverRate: null,
  totalSavedFarms: 0,
  viewFarmBoundariesVisible: false,
  walkingFarmKey: null
});

export const reducer = createReducer(
  initialState,
  on(fromWalkingFarmActions.setWalkingFarm, (state, { payload }) => {
    const newState: State = {
      ...state,
      savedFarms: map(state.savedFarms, (item) => ({
        ...item,
        name: item.id === payload.farmId ? payload.walkingFarm.name : item.name
      }))
    };

    const farm = selectEntities(state)[payload.farmId];

    if (!isUndefined(farm)) {
      return adapter.updateOne(
        {
          changes: {
            ...payload,
            phoneAndEmailSelectedPropertyIds: farm.phoneAndEmailSelectedPropertyIds
          },
          id: payload.farmId
        },
        newState
      );
    }

    return adapter.setOne(payload, newState);
  }),
  on(fromWalkingFarmActions.toggleDrawMode, (state) => ({
    ...state,
    drawFarmCenter: initialState.drawFarmCenter,
    drawFarmCompleted: initialState.drawFarmCompleted,
    drawFarmPoints: initialState.drawFarmPoints,
    drawFarmRadius: initialState.drawFarmRadius,
    drawFarmRadiusInMeters: initialState.drawFarmRadiusInMeters,
    drawMode: !state.drawMode,
    drawModeType: initialState.drawModeType
  })),
  on(fromWalkingFarmActions.addDrawFarmPoint, (state, { payload }) => {
    const newState = { ...state };
    if (!state.drawFarmCompleted && newState.drawFarmPoints.length < 12) {
      newState.drawFarmPoints = [...state.drawFarmPoints, payload.point];
    }
    return newState;
  }),
  on(fromWalkingFarmActions.addDrawFarmFinalPoint, (state, { payload }) => {
    const newState = { ...state };
    if (
      !state.drawFarmCompleted &&
      state.drawFarmPoints.length > 2 &&
      booleanEqual(
        point([state.drawFarmPoints[0].lng, state.drawFarmPoints[0].lat]),
        point([payload.point.lng, payload.point.lat])
      )
    ) {
      newState.drawFarmPoints = [...state.drawFarmPoints, payload.point];
      newState.drawFarmCompleted = true;
    }
    return newState;
  }),
  on(fromWalkingFarmActions.undoDrawFarmPoint, (state) => {
    const newState = { ...state };
    if (state.drawFarmPoints.length) {
      newState.drawFarmPoints = slice(state.drawFarmPoints, 0, state.drawFarmPoints.length - 1);
      newState.drawFarmCompleted = false;
    }
    return newState;
  }),
  on(fromWalkingFarmActions.clearDrawFarm, (state) => ({
    ...state,
    drawFarmCenter: initialState.drawFarmCenter,
    drawFarmCompleted: initialState.drawFarmCompleted,
    drawFarmPoints: initialState.drawFarmPoints,
    drawFarmRadius: initialState.drawFarmRadius,
    drawFarmRadiusInMeters: initialState.drawFarmRadiusInMeters
  })),
  on(fromWalkingFarmActions.autoFillDrawFarm, (state) => {
    const newState = { ...state };
    if (!state.drawFarmCompleted && state.drawFarmPoints.length > 2) {
      newState.drawFarmPoints = [...state.drawFarmPoints, state.drawFarmPoints[0]];
      newState.drawFarmCompleted = true;
    }
    return newState;
  }),
  on(fromWalkingFarmActions.newConfiguredFarm, (state, { payload }) => {
    const newState = { ...state };
    newState.drawMode = true;
    newState.mainSelectedTab = WalkingFarmMainTabEnum.New;
    newState.drawModeType = has(payload.shapeCoordinates, 'points')
      ? WalkingFarmNewTabEnum.Polygon
      : WalkingFarmNewTabEnum.Radius;
    newState.drawFarmRadius = initialState.drawFarmRadius;
    newState.drawFarmRadiusInMeters = initialState.drawFarmRadiusInMeters;
    newState.drawFarmCenter = initialState.drawFarmCenter;
    newState.drawFarmCompleted = initialState.drawFarmCompleted;
    newState.drawFarmPoints = initialState.drawFarmPoints;

    if (newState.drawModeType === WalkingFarmNewTabEnum.Polygon) {
      newState.drawFarmPoints = map((<PolygonFarmCoordinates>payload.shapeCoordinates).points, (point) => ({
        lat: point.x,
        lng: point.y
      }));
      const bounds = new google.maps.LatLngBounds();
      each((<PolygonFarmCoordinates>payload.shapeCoordinates).points, (point) => {
        bounds.extend({ lat: point.x, lng: point.y });
      });
      newState.drawFarmCenter = bounds.getCenter().toJSON();
      newState.drawFarmCompleted = true;
    } else {
      newState.drawFarmCenter = {
        lat: (<RadiusFarmCoordinates>payload.shapeCoordinates).latitude,
        lng: (<RadiusFarmCoordinates>payload.shapeCoordinates).longitude
      };
      newState.drawFarmRadius = (<RadiusFarmCoordinates>payload.shapeCoordinates).radius;
      newState.drawFarmRadiusInMeters = getRadiusInMetersFromMiles(newState.drawFarmRadius);
    }
    return newState;
  }),
  on(fromWalkingFarmActions.refreshKey, (state) => ({
    ...state,
    walkingFarmKey: null
  })),
  on(fromWalkingFarmActions.refreshKeySuccess, (state, { payload }) => ({
    ...state,
    walkingFarmKey: payload.key
  })),
  on(fromWalkingFarmActions.resetKey, (state) => ({
    ...state,
    walkingFarmKey: initialState.walkingFarmKey
  })),
  on(fromWalkingFarmActions.loadFarmSummarySuccess, (state, { payload }) => ({
    ...state,
    summaryErrorMessage: null,
    summaryMessages: payload.messages,
    summaryTotal: payload.total,
    summaryTurnoverRate: payload.turnoverRate
  })),
  on(fromWalkingFarmActions.saveFarmFailure, (state, { payload }) => ({
    ...state,
    summaryErrorMessage: payload.error.message
  })),
  on(fromWalkingFarmActions.saveFarmSuccess, (state) => ({
    ...state,
    drawFarmCompleted: initialState.drawFarmCompleted,
    drawFarmPoints: initialState.drawFarmPoints,
    drawMode: initialState.drawMode,
    mainSelectedTab: WalkingFarmMainTabEnum.Saved,
    summaryMessages: initialState.summaryMessages,
    summaryTotal: initialState.summaryTotal,
    summaryTurnoverRate: initialState.summaryTurnoverRate,
    totalSavedFarms: state.totalSavedFarms + 1
  })),
  on(fromWalkingFarmActions.setMainSelectedTab, (state, { payload }) => ({
    ...state,
    mainSelectedTab: payload.selectedTab
  })),
  on(fromWalkingFarmActions.loadSavedFarmsListSuccess, (state, { payload }) => {
    const missingFarmIds: string[] = [];
    forEach(state.ids, (farmId) => {
      const farm = find(payload, (item) => item.id === farmId);
      if (!farm) {
        missingFarmIds.push(`${farmId}`);
      }
    });

    return adapter.removeMany(missingFarmIds, {
      ...state,
      savedFarms: map(payload, (item) => {
        let turnoverRate = parseFloat(item.turnoverRate);
        if (isNaN(turnoverRate)) {
          turnoverRate = 0;
        }
        return {
          alertsEnabled: item.alertsEnabled,
          createdAt: item.reportDate,
          id: item.id,
          isTurnoverTrendUp: item.isTurnoverTrendUp,
          name: item.name,
          propertiesWithUnreadAlerts: item.propertiesWithUnreadAlerts,
          showTurnoverTrend: item.showTurnoverTrend,
          totalProperties: item.propertyCount,
          turnoverRate: turnoverRate
        };
      }),
      savedFarmsTextFilter: '',
      totalSavedFarms: payload.length
    });
  }),
  on(fromWalkingFarmActions.updateSavedFarmsListFilter, (state, { payload }) => ({
    ...state,
    savedFarmsTextFilter: payload.text
  })),
  on(
    fromSettingActions.refreshReferralCodeSuccess,
    fromSettingActions.updateReferralCodeSuccess,
    fromSettingActions.loadSettingsSuccess,
    fromPersistedStateActions.migratePersistedOldStateReferralCodeSuccess,
    (state, { payload }) => ({
      ...state,
      filterAvailablePropertyTypes: [
        ...cloneDeep(commonPropertyTypesFilters),
        ...(isBlackKnight(payload.data.provider.name) ? cloneDeep(blackKnightPropertyTypesFilters) : [])
      ]
    })
  ),
  on(fromWalkingFarmActions.updateNewFarmFilters, (state, { payload }) => ({
    ...state,
    filterByMaxBathrooms: payload.filterByMaxBathrooms,
    filterByMaxBedrooms: payload.filterByMaxBedrooms,
    filterByMaxLivingArea: payload.filterByMaxLivingArea,
    filterByMinBathrooms: payload.filterByMinBathrooms,
    filterByMinBedrooms: payload.filterByMinBedrooms,
    filterByMinLivingArea: payload.filterByMinLivingArea,
    filterByNoSalesWithinYears: payload.filterByNoSalesWithinYears,
    filterByOwnerOccupied: payload.filterByOwnerOccupied,
    filterByPropertyType: payload.filterByPropertyType,
    filtersAppliedNumber: compact([
      payload.filterByMaxBathrooms ?? payload.filterByMinBathrooms,
      payload.filterByMaxBedrooms ?? payload.filterByMinBedrooms,
      payload.filterByMaxLivingArea ?? payload.filterByMinLivingArea,
      payload.filterByOwnerOccupied,
      payload.filterByPropertyType.length,
      payload.filterByNoSalesWithinYears
    ]).length
  })),
  on(fromWalkingFarmActions.clearNewFarmFilters, (state) => ({
    ...state,
    filterByMaxBathrooms: initialState.filterByMaxBathrooms,
    filterByMaxBedrooms: initialState.filterByMaxBedrooms,
    filterByMaxLivingArea: initialState.filterByMaxLivingArea,
    filterByMinBathrooms: initialState.filterByMinBathrooms,
    filterByMinBedrooms: initialState.filterByMinBedrooms,
    filterByMinLivingArea: initialState.filterByMinLivingArea,
    filterByNoSalesWithinYears: initialState.filterByNoSalesWithinYears,
    filterByOwnerOccupied: initialState.filterByOwnerOccupied,
    filterByPropertyType: initialState.filterByPropertyType,
    filterBySaleTypes: initialState.filterBySaleTypes,
    filtersAppliedNumber: 0
  })),
  on(fromWalkingFarmActions.deleteSavedFarm, (state, { payload }) => {
    const newState = { ...state };
    newState.savedFarms = filter(newState.savedFarms, (farm) => farm.id !== payload.id);
    newState.totalSavedFarms = newState.savedFarms.length;
    return adapter.removeOne(payload.id, newState);
  }),
  on(fromWalkingFarmActions.loadPropertyAlerts, (state) => ({
    ...state,
    propertyFarmAlertsLastAlertDate: initialState.propertyFarmAlertsLastAlertDate,
    propertyFarmAlertsList: initialState.propertyFarmAlertsList,
    propertyFarmAlertsLoaded: initialState.propertyFarmAlertsLoaded
  })),
  on(fromWalkingFarmActions.loadPropertyAlertsSuccess, (state, { payload }) => ({
    ...state,
    propertyFarmAlertsLastAlertDate: payload.lastAlertDate,
    propertyFarmAlertsList: payload.alerts,
    propertyFarmAlertsLoaded: true
  })),
  on(fromWalkingFarmActions.loadAlerts, (state) => ({
    ...state,
    farmAlertsEnabled: initialState.farmAlertsEnabled,
    farmAlertsFarmName: initialState.farmAlertsFarmName,
    farmAlertsLastAlertDate: initialState.farmAlertsLastAlertDate,
    farmAlertsList: initialState.farmAlertsList,
    farmAlertsLoaded: initialState.farmAlertsLoaded
  })),
  on(fromWalkingFarmActions.loadAlertsSuccess, (state, { payload }) => ({
    ...state,
    farmAlertsEnabled: payload.alertsEnabled,
    farmAlertsFarmName: payload.name,
    farmAlertsLastAlertDate: payload.lastAlertDate,
    farmAlertsList: payload.alerts,
    farmAlertsLoaded: true
  })),
  on(fromWalkingFarmActions.markAlertsAsRead, (state, { payload }) => {
    const newState = {
      ...state,
      farmAlertsList: map(state.farmAlertsList, (item) => {
        const result = { ...item };
        const payloadAlert = find(payload.unreadAlerts, (alert) => alert.alertId === item.id);
        if (payloadAlert) {
          result.isRead = true;
        }
        return result;
      }),
      propertyFarmAlertsList: map(state.propertyFarmAlertsList, (item) => {
        const result = { ...item };
        const payloadAlert = find(payload.unreadAlerts, (alert) => alert.alertId === item.id);
        if (payloadAlert) {
          result.isRead = true;
        }
        return result;
      })
    };
    let farm = selectEntities(newState)[payload.farmId];

    if (!isUndefined(farm)) {
      farm = cloneDeep(farm);
      const unreadAlertCounts = countBy(payload.unreadAlerts, 'propertyId');
      forEach(farm.walkingFarm.properties, (property, index) => {
        const count = unreadAlertCounts[`${property.A000_PropertyNumber}`] || 0;
        if (
          farm &&
          !isUndefined(farm.walkingFarm.properties[index].unreadFarmAlerts) &&
          farm.walkingFarm.properties[index].unreadFarmAlerts > 0
        ) {
          farm.walkingFarm.properties[index].unreadFarmAlerts -= count;
        }
      });
      return adapter.updateOne({ changes: farm, id: farm.farmId }, { ...newState });
    } else {
      return { ...newState };
    }
  }),
  on(fromWalkingFarmActions.updatePropertiesOrderSuccess, (state, { payload }) => {
    let farm = selectEntities(state)[payload.farmId];
    if (!isUndefined(farm)) {
      farm = cloneDeep(farm);
      farm.walkingFarm.properties = sortBy(farm.walkingFarm.properties, (item) => {
        const index = findIndex(payload.order, (id) => id === `${item.A000_PropertyNumber}`);
        return index > -1 ? index : farm?.walkingFarm.properties.length;
      });
      return adapter.updateOne({ changes: farm, id: farm.farmId }, { ...state });
    } else {
      return { ...state };
    }
  }),
  on(fromWalkingFarmActions.toggleAlertsEnabledSuccess, (state) => ({
    ...state,
    farmAlertsEnabled: !state.farmAlertsEnabled
  })),
  on(fromWalkingFarmActions.toggleBoundariesVisible, (state) => ({
    ...state,
    viewFarmBoundariesVisible: !state.viewFarmBoundariesVisible
  })),
  on(fromWalkingFarmActions.updatePropertySuccess, (state, { payload: { farmId, property } }) => {
    let farm = selectEntities(state)[farmId];
    if (!isUndefined(farm)) {
      forEach(farm.walkingFarm.properties, (item, index) => {
        if (item.A000_PropertyNumber === property.A000_PropertyNumber) {
          if (!isUndefined(farm)) {
            farm = cloneDeep(farm);
            farm.walkingFarm.properties[index] = {
              ...property
            };
          }
          return;
        }
      });
      return adapter.updateOne({ changes: farm, id: farm.farmId }, { ...state });
    } else {
      return { ...state };
    }
  }),
  on(
    fromWalkingFarmActions.updateViewFarmFilters,
    (state, { payload: { farmId, filterByLeadStatus, filterByOwnerStatus } }) => {
      let farm = selectEntities(state)[farmId];
      if (!isUndefined(farm)) {
        farm = cloneDeep(farm);
        farm.filterByOwnerStatus = filterByOwnerStatus;
        farm.filterByLeadStatus = filterByLeadStatus;
        farm.filtersAppliedNumber =
          (farm.filterByLeadStatus.length > 0 ? 1 : 0) +
          (farm.filterByOwnerStatus === WalkingFarmPropertiesOwnerStatusFilterEnum.All ? 0 : 1);
        return adapter.updateOne({ changes: farm, id: farm.farmId }, { ...state });
      } else {
        return { ...state };
      }
    }
  ),
  on(fromWalkingFarmActions.clearViewFarmFilters, (state, { payload: { farmId } }) => {
    let farm = selectEntities(state)[farmId];
    if (!isUndefined(farm)) {
      farm = cloneDeep(farm);
      farm.filterByOwnerStatus = WalkingFarmPropertiesOwnerStatusFilterEnum.All;
      farm.filterByLeadStatus = [];
      farm.filtersAppliedNumber = 0;
      return adapter.updateOne({ changes: farm, id: farm.farmId }, { ...state });
    } else {
      return { ...state };
    }
  }),
  on(fromWalkingFarmActions.setDrawModeType, (state, { payload: { type } }) => ({
    ...state,
    drawModeType: type
  })),
  on(fromWalkingFarmActions.setDrawFarmRadius, (state, { payload: { radius } }) => ({
    ...state,
    drawFarmRadius: radius,
    drawFarmRadiusInMeters: getRadiusInMetersFromMiles(radius)
  })),
  on(fromWalkingFarmActions.setDrawFarmCenter, (state, { payload: { center } }) => ({
    ...state,
    drawFarmCenter: center
  })),
  on(fromPersistedStateActions.persistedStateReset, fromPersistedStateActions.loadPersistedStateFailure, () => ({
    ...initialState
  })),
  on(fromPersistedStateActions.loadPersistedStateSuccess, (state, { payload }) => ({
    ...state,
    ...payload[walkingFarmFeatureKey]
  })),
  on(
    fromWalkingFarmActions.updatePhoneAndEmailSelectedPropertyIds,
    (state, { payload: { farmId, phoneAndEmailSelectedPropertyIds } }) => {
      let farm = selectEntities(state)[farmId];
      if (!isUndefined(farm)) {
        farm = cloneDeep(farm);
        farm.phoneAndEmailSelectedPropertyIds = phoneAndEmailSelectedPropertyIds;
        return adapter.updateOne({ changes: farm, id: farm.farmId }, { ...state });
      } else {
        return { ...state };
      }
    }
  ),
  on(fromWalkingFarmActions.geocodeLocationSuccess, (state, { payload }) => ({
    ...state,
    addressSearchLocation: payload.result.geometry.location.toJSON(),
    addressSearchText: payload.result.formatted_address
  })),
  on(fromWalkingFarmActions.resetSearch, fromPropertyProfileActions.geocodeAddressLocation, (state) => ({
    ...state,
    addressSearchLocation: initialState.addressSearchLocation,
    addressSearchText: initialState.addressSearchText
  }))
);

const { selectEntities } = adapter.getSelectors();

export const walkingFarmSelectEntities = selectEntities;
